import { METRIC_INPUTS_NAV_LABEL, METRIC_INPUTS_PATH } from './inputs/index.js';
import { DATA_ISSUES_NAV_LABEL, DATA_ISSUES_PATH } from './issues/index.js';

/**
 * The Data area's section tabs. The site navbar (SiteTopbar) is shared with
 * every other page; inside `/data` these tabs are the only second level, the
 * same pattern the portal's Inventory view uses for its tabs.
 */
export type DataSectionId = 'home' | 'dashboards' | 'metrics' | 'tables' | 'contracts' | 'sources' | 'access' | 'more';

export interface DataSection {
  id: Exclude<DataSectionId, 'more' | 'dashboards'>;
  label: string;
  to: string;
}

export const DATA_SECTIONS: DataSection[] = [
  { id: 'home', label: 'Home', to: '/data' },
  { id: 'metrics', label: 'Metrics', to: '/data/metrics' },
  { id: 'tables', label: 'Tables', to: '/data/tables' },
  { id: 'contracts', label: 'Contracts', to: '/data/contracts' },
  { id: 'sources', label: 'Sources', to: '/data/sources' },
  { id: 'access', label: 'Access', to: '/data/access' },
];

export interface DataToolLink {
  id: 'datasets' | 'lineage' | 'query' | 'saved' | 'charts' | 'guide' | 'inputs' | 'connect' | 'skills' | 'you' | 'ask' | 'issues';
  label: string;
  to: string;
  description: string;
}

/**
 * The Data tools. Before the 2026-10-01 redesign these were first-level items
 * in a left sidebar; the redesign hid them under "More". They are back as a
 * persistent second row under the section tabs so they are one click away on
 * every Data page (and the Data guide has its own entry, see the shell).
 */
export const DATA_TOOL_LINKS: DataToolLink[] = [
  { id: 'datasets', label: 'Datasets', to: '/data/datasets', description: 'Tables grouped by business question' },
  { id: 'lineage', label: 'Lineage', to: '/data/lineage', description: 'Where data comes from and where it goes' },
  { id: 'query', label: 'Query editor', to: '/data/query', description: 'Write SQL against governed tables' },
  { id: 'saved', label: 'Saved', to: '/data/saved', description: 'Your saved queries and views' },
  { id: 'charts', label: 'Charts', to: '/data/charts', description: 'Saved custom charts and charts shared with you' },
  { id: 'guide', label: 'Guide', to: '/data/guide', description: 'How the data platform fits together' },
  { id: 'inputs', label: METRIC_INPUTS_NAV_LABEL, to: METRIC_INPUTS_PATH, description: 'Submit and approve manual inputs, like podcast spend' },
  // Folded in from the retired Context OS page (`/context`) on 2026-10-01.
  { id: 'connect', label: 'Use with Claude', to: '/data/connect', description: 'Connect Claude, Claude Code, Codex or ChatGPT to Mars Men data' },
  { id: 'skills', label: 'Skills', to: '/data/skills', description: 'Shared skills and agents your AI client can use' },
  { id: 'you', label: 'You', to: '/data/you', description: 'Your access, usage, budget, Google Drive and activity' },
  { id: 'ask', label: 'Ask', to: '/data/ask', description: 'Ask a question in plain English' },
  { id: 'issues', label: DATA_ISSUES_NAV_LABEL, to: DATA_ISSUES_PATH, description: 'Wrong or missing answers people reported, and what happened to them' },
];

/** @deprecated kept for older imports; the tools are no longer behind a "More" menu. */
export type DataMoreLink = DataToolLink;
export const DATA_MORE_LINKS = DATA_TOOL_LINKS;

const under = (pathname: string, prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/** The section tab a `/data` path belongs to. */
export const dataSectionFor = (pathname: string): DataSectionId => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/data' || under(path, '/data/search')) return 'home';
  // Team dashboards have their own tab (a dropdown rendered by DataHeader, not a DATA_SECTIONS link).
  if (under(path, '/data/dashboards')) return 'dashboards';
  if (under(path, '/data/metrics') || under(path, '/data/inputs')) return 'metrics';
  if (under(path, '/data/tables') || under(path, '/data/browse') || under(path, '/data/datasets')) return 'tables';
  if (under(path, '/data/contracts')) return 'contracts';
  if (under(path, '/data/sources')) return 'sources';
  if (under(path, '/data/access')) return 'access';
  return 'more';
};

/** The tool link a `/data` path belongs to, if any. */
export const dataToolFor = (pathname: string): DataToolLink['id'] | null => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (under(path, '/data/datasets')) return 'datasets';
  if (under(path, '/data/lineage')) return 'lineage';
  if (under(path, '/data/query')) return 'query';
  if (under(path, '/data/saved')) return 'saved';
  if (under(path, '/data/charts')) return 'charts';
  if (under(path, '/data/guide')) return 'guide';
  if (under(path, '/data/inputs')) return 'inputs';
  if (under(path, '/data/connect')) return 'connect';
  if (under(path, '/data/skills')) return 'skills';
  if (under(path, '/data/you')) return 'you';
  if (under(path, '/data/ask')) return 'ask';
  if (under(path, DATA_ISSUES_PATH)) return 'issues';
  return null;
};

/**
 * Old Data paths that moved. `/data/browse` became the Tables section at
 * `/data/tables`; query strings (filters, grouping) carry over.
 */
export const DATA_PATH_REDIRECTS: Record<string, string> = {
  '/data/browse': '/data/tables',
};

export const redirectedDataPath = (pathname: string, search = ''): string | null => {
  const target = DATA_PATH_REDIRECTS[pathname.replace(/\/+$/, '')];
  return target ? `${target}${search}` : null;
};

/** `/#access/data` on the portal is the shortcut to the Data access matrix. */
export const DATA_ACCESS_PATH = '/data/access';
export const isDataAccessHash = (hash: string): boolean => /^#\/?access\/data(?:[/?].*)?$/.test(hash);
